<template>
  <div
    class="group relative inline select-none"
    :class="{ 'cursor-pointer': props.copyOn === 'all' || (props.masked && !isVisible) }"
    @click="handleClick()"
  >
    <span ref="contentSpan">
      <slot v-if="isVisible">{{ content }}</slot>
      <template v-else>{{ maskedContent }}</template>
    </span>
    <font-awesome-icon
      v-if="derivedIconMode !== 'disabled'"
      :icon="currentIcon"
      class="ml-1 cursor-pointer"
      :class="{
        invisible: derivedIconMode === 'hover',
        'group-hover:visible': derivedIconMode === 'hover',
        'pl-2': derivedIconMode === 'permanent',
        'float-right': derivedIconMode === 'permanent-right-align',
        'text-xl': props.iconSize === 'large',
      }"
      @click.stop="handleClick('icon')"
    />
  </div>
</template>

<script lang="ts" setup>
import { faCheck, faCopy, faEye } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useEventListener } from "@vueuse/core";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useTooltips } from "../tooltips";

const { t } = useI18n();

type CopyOn = "all" | "icon";
/**
 * `disabled`: Never show the copy icon
 * `hover`: Display the icon when hovering over on Desktop, and permanently if the device has a touchscreen
 * `permanent`: Always display the icon
 * `permanent-right-align`: Always display the icon, and float it to the right
 */
type IconMode = "disabled" | "hover" | "permanent" | "permanent-right-align";
type IconSize = "large";

const props = withDefaults(
  defineProps<{
    content: unknown;
    copyOn: CopyOn;
    /**
     * Configure when to show the icon.
     */
    iconMode: IconMode;
    iconSize?: IconSize;
    message?: string;
    duration?: number;
    masked?: boolean;
  }>(),
  {
    copyOn: "all",
    iconMode: "disabled",
    iconSize: "large",
    message: undefined,
    duration: 1000,
    masked: false,
  },
);

const contentSpan = ref<HTMLSpanElement>();

const { pushTooltip } = useTooltips();

const isVisible = ref(!props.masked);
const maskingTimer = ref<number | null>(null);
const checkmarkTimer = ref<number | null>(null);
const checkmarkVisible = ref(false);

const currentIcon = computed(() => {
  if (checkmarkVisible.value) {
    return faCheck;
  }
  if (!props.masked) {
    return faCopy;
  }
  if (isVisible.value) {
    return faCopy;
  }
  return faEye;
});

const message = computed(() => {
  return props.message || t("ngp/clipboard/copy");
});

const maskedContent = "●●●●●●";

function startMaskingTimer() {
  // Clear existing timers
  if (maskingTimer.value !== null) {
    window.clearTimeout(maskingTimer.value);
  }

  maskingTimer.value = window.setTimeout(() => {
    isVisible.value = false;
    maskingTimer.value = null;
  }, 5000);
}

function startCheckmarkTimer() {
  // Clear existing timers
  if (checkmarkTimer.value !== null) {
    window.clearTimeout(checkmarkTimer.value);
  }

  checkmarkVisible.value = true;
  checkmarkTimer.value = window.setTimeout(() => {
    checkmarkVisible.value = false;
    checkmarkTimer.value = null;
  }, 3000);
}

// Watch for changes in the masked prop, clear the timer if it's made visible
watch(
  () => props.masked,
  (newMaskedValue) => {
    isVisible.value = !newMaskedValue;
    if (!newMaskedValue && maskingTimer.value !== null) {
      window.clearTimeout(maskingTimer.value);
      maskingTimer.value = null;
    }
  },
);

// Clear timer if content no longer visible
watch(isVisible, (newValue) => {
  if (props.masked) {
    if (newValue) {
      startMaskingTimer();
    } else if (maskingTimer.value !== null) {
      window.clearTimeout(maskingTimer.value);
      maskingTimer.value = null;
    }
  }
});

async function copyContent() {
  if (!props.content) return;
  await navigator.clipboard.writeText(props.content.toString());
  startCheckmarkTimer();
  if (contentSpan.value) {
    let { x, y } = contentSpan.value.getBoundingClientRect();
    y += contentSpan.value?.offsetHeight;
    pushTooltip({
      x,
      y,
      text: message.value,
      duration: props.duration,
    });
  }
}

function handleClick(clickType) {
  if (props.masked && !isVisible.value) {
    isVisible.value = true;
  } else if (props.copyOn === "all" || (props.copyOn === "icon" && clickType === "icon")) {
    copyContent();
  }
}

const isTouchscreen = ref(false);

const updateIsTouchscreen = () => (isTouchscreen.value = window.matchMedia("(any-pointer: coarse)").matches);

useEventListener(window, "resize", updateIsTouchscreen);
onMounted(updateIsTouchscreen);

// Clear timers when unmounted
onUnmounted(() => {
  if (maskingTimer.value !== null) {
    window.clearTimeout(maskingTimer.value);
  }
  if (checkmarkTimer.value !== null) {
    window.clearTimeout(checkmarkTimer.value);
  }
});

const derivedIconMode = computed<IconMode>(() => {
  if (props.iconMode === "hover" && isTouchscreen.value) {
    return "permanent";
  }
  return props.iconMode;
});
</script>
